import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  output,
  PLATFORM_ID,
  Renderer2,
  signal,
  untracked,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import {
  IOutlet,
  IOutletAccordionOptions,
  IScheduleOverride,
  OUTLET_SCHEDULE_TYPES,
} from '@cakeiteasy/web-utils';
import type * as MapLibre from 'maplibre-gl';

import { UiOutletsAccordionComponent } from '../ui-outlets-accordion/ui-outlets-accordion.component';
import {
  configureMapLibreWorker,
  createBounds,
  getMapLibreOutletMarkerHtml,
  hasCoordinates,
  latLng,
  simplifyMapLibreOutletStyle,
} from '../../helpers/maplibre.helper';
import { DEFAULT_MAPLIBRE_CONFIG } from '../../constants/maplibre.constants';
import { MapMarkerThemeService } from '../../services/map-marker-theme.service';

type OutletsMapMobileView = 'map' | 'selected' | 'list';

interface ISheetDrag {
  pointerId: number;
  startY: number;
  startHeight: number;
  maxHeight: number;
  isMoved: boolean;
}

// Share of the host the sheet covers in the `selected` view; keep in sync with `.is-selected` in the scss.
const SELECTED_SHEET_HEIGHT_RATIO = 0.5;
const FIT_BOUNDS_PADDING: MapLibre.PaddingOptions = { top: 60, left: 40, right: 40, bottom: 100 };
const FIT_BOUNDS_MAX_ZOOM = 16;
// Zoom for an outlet picked out of several (the desktop map uses initialZoom, 13). A single outlet keeps
// initialZoom (NS3-9151) whether selected or not, so closing the sheet doesn't zoom out.
const SELECTED_OUTLET_ZOOM = 16;
// Pointer travel before a press on the sheet handle counts as a drag rather than a tap.
const SHEET_DRAG_START_PX = 8;
// Drag distance that commits the sheet to the next state; shorter drags snap back.
const SHEET_SWIPE_PX = 40;

@Component({
  standalone: true,
  selector: 'ui-outlets-map-mobile',
  templateUrl: './ui-outlets-map-mobile.component.html',
  styleUrls: ['./ui-outlets-map-mobile.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [UiOutletsAccordionComponent, TranslateModule],
  host: { class: 'ui-outlets-map-mobile' },
})
export class UiOutletsMapMobileComponent implements AfterViewInit, OnDestroy {
  readonly outlets = input<IOutlet[]>();
  readonly disabledOutletIds = input<number[]>([]);
  readonly scheduleOverridesByOutletId = input<{ [outletId: number]: IScheduleOverride[] }>({});
  readonly scheduleType = input<string>(OUTLET_SCHEDULE_TYPES.WORKING_DAYS);
  readonly outletAccordionOptions = input<IOutletAccordionOptions>();
  readonly isOutletSelectionStateEnabled = input(true);
  readonly showNext = input<boolean>();
  readonly nextText = input<string>();
  readonly blockWheelScrolling = input<boolean>();

  readonly onOutletChanged = output<IOutlet>();
  readonly onNext = output<IOutlet>();

  readonly view = signal<OutletsMapMobileView>('map');
  readonly selectedOutlet = signal<IOutlet>(null);
  // Sheet height while it follows the finger; null hands the height back to the view classes.
  readonly dragHeight = signal<number | null>(null);
  // The map is already on screen, so the accordion's own per-outlet map is redundant here.
  readonly accordionOptions = computed<IOutletAccordionOptions>(() => ({
    ...this.outletAccordionOptions(),
    hideMap: true,
  }));

  private readonly _host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly _renderer = inject(Renderer2);
  private readonly _mapContainer = viewChild.required<ElementRef<HTMLDivElement>>('mapContainer');
  private readonly _sheetScroll = viewChild<ElementRef<HTMLDivElement>>('sheetScroll');
  private readonly _markerTheme = inject(MapMarkerThemeService);
  private readonly _platformId = inject(PLATFORM_ID);

  private _maplibre: typeof MapLibre;
  private _map: MapLibre.Map;
  private _markers = new Map<number, MapLibre.Marker>();
  private _isViewInitialized = false;
  private _isMapInitializing = false;
  private _isDestroyed = false;
  private _sheetDrag: ISheetDrag | null = null;
  private _sheetDragUnlisteners: (() => void)[] = [];
  private _isMapBtnClickSuppressed = false;

  constructor() {
    effect(() => {
      const outlets = this.outlets();

      if (outlets) {
        untracked(() => this._onOutletsChanged(outlets));
      }
    });

    effect(() => {
      this.selectedOutlet();
      this.disabledOutletIds();
      this._markerTheme.theme();
      untracked(() => this._renderMarkers());
    });

    effect(() => {
      const blockWheelScrolling = this.blockWheelScrolling();
      untracked(() => this._syncScrollWheel(blockWheelScrolling));
    });
  }

  async ngAfterViewInit(): Promise<void> {
    if (!isPlatformBrowser(this._platformId)) {
      return;
    }

    this._isViewInitialized = true;
    await this._tryInitMap();
  }

  ngOnDestroy(): void {
    this._isDestroyed = true;
    this._stopSheetDrag();
    this._clearMarkers();
    this._map?.remove();
  }

  public showList(): void {
    // One outlet is the whole list: it opens as the half sheet with that outlet selected, like on start.
    if (this._hasSingleOutlet()) {
      const outlet = this.outlets()[0];

      this.view.set('selected');
      this._select(outlet);
      this._centerOn(outlet);
      return;
    }

    this.view.set('list');
  }

  public showMap(): void {
    this.view.set('map');
    this._select(null);
    this._fitBounds(this.outlets() || []);
  }

  public onMapBtnClick(): void {
    if (this._isMapBtnClickSuppressed) {
      this._isMapBtnClickSuppressed = false;
      return;
    }

    this.showMap();
  }

  public onHandlePointerDown(event: PointerEvent): void {
    if (this._sheetDrag || (event.pointerType === 'mouse' && event.button !== 0)) {
      return;
    }

    const sheetWrap = (event.currentTarget as HTMLElement).closest<HTMLElement>('.ui-outlets-map-mobile__sheet-wrap');

    this._isMapBtnClickSuppressed = false;
    this._sheetDrag = {
      pointerId: event.pointerId,
      startY: event.clientY,
      startHeight: sheetWrap.getBoundingClientRect().height,
      // A single outlet has no full-height state, so the sheet can't be pulled above where it is.
      maxHeight: this._hasSingleOutlet()
        ? sheetWrap.getBoundingClientRect().height
        : this._host.nativeElement.clientHeight,
      isMoved: false,
    };
    // Tracked on the document: a quick mouse drag leaves the handle before its first move event.
    this._sheetDragUnlisteners = [
      this._renderer.listen('document', 'pointermove', (e: PointerEvent) => this._onSheetDragMove(e)),
      this._renderer.listen('document', 'pointerup', (e: PointerEvent) => this._onSheetDragEnd(e)),
      this._renderer.listen('document', 'pointercancel', (e: PointerEvent) => this._onSheetDragEnd(e, true)),
    ];
  }

  public onAccordionOutletChanged(outlet: IOutlet): void {
    if (!outlet) {
      this._select(null);
      this._fitBounds(this.outlets() || []);
      return;
    }

    this.view.set('selected');
    this._select(outlet);
    this._centerOn(outlet);
  }

  public scrollSheetTo(targetId: string): void {
    const container = this._sheetScroll()?.nativeElement;
    const target = container?.querySelector<HTMLElement>(`#${targetId}`);

    if (!target) {
      return;
    }

    const top = target.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;
    container.scrollTo({ top: Math.max(0, top - 8), behavior: 'smooth' });
  }

  private _onSheetDragMove(event: PointerEvent): void {
    const drag = this._sheetDrag;

    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }

    const deltaY = event.clientY - drag.startY;

    if (!drag.isMoved && Math.abs(deltaY) < SHEET_DRAG_START_PX) {
      return;
    }

    drag.isMoved = true;
    this.dragHeight.set(Math.min(Math.max(drag.startHeight - deltaY, 0), drag.maxHeight));
  }

  private _onSheetDragEnd(event: PointerEvent, isCancelled = false): void {
    const drag = this._sheetDrag;

    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }

    this._stopSheetDrag();

    if (!drag.isMoved || isCancelled) {
      return;
    }

    // A drag released over the button would otherwise reach it as a tap.
    this._isMapBtnClickSuppressed = true;
    const deltaY = event.clientY - drag.startY;

    // Swiping down steps back one state: full list → half sheet → map.
    if (deltaY <= -SHEET_SWIPE_PX) {
      if (!this._hasSingleOutlet()) {
        this.showList();
      }
    } else if (deltaY >= SHEET_SWIPE_PX && this.view() === 'list') {
      this.view.set('selected');
    } else if (deltaY >= SHEET_SWIPE_PX) {
      this.showMap();
    }
  }

  private _stopSheetDrag(): void {
    this._sheetDragUnlisteners.forEach((unlisten) => unlisten());
    this._sheetDragUnlisteners = [];
    this._sheetDrag = null;
    this.dragHeight.set(null);
  }

  private _onMarkerClick(outlet: IOutlet): void {
    this.view.set('selected');
    this._select(outlet);
    this._centerOn(outlet);
  }

  private _select(outlet: IOutlet | null): void {
    if (this.selectedOutlet()?.id === outlet?.id) {
      return;
    }

    this.selectedOutlet.set(outlet);
    this.onOutletChanged.emit(outlet);
  }

  private _onOutletsChanged(outlets: IOutlet[]): void {
    const selected = this.selectedOutlet();

    if (outlets.length === 1) {
      this.view.set('selected');
      this._select(outlets[0]);
    } else if (selected && !outlets.some((outlet) => outlet.id === selected.id)) {
      this.view.set('map');
      this._select(null);
    }

    if (!this._map) {
      void this._tryInitMap();
      return;
    }

    this._renderMarkers();

    if (this.selectedOutlet()) {
      this._centerOn(this.selectedOutlet());
    } else {
      this._fitBounds(outlets);
    }
  }

  private _renderMarkers(): void {
    if (!this._map || !this._maplibre || !this.outlets()) {
      return;
    }

    this._clearMarkers();

    this.outlets().filter(hasCoordinates).forEach((outlet) => {
      const isActive = this.selectedOutlet()?.id === outlet.id;
      const isDisabled = Boolean(this.disabledOutletIds()?.includes(outlet.id));
      const element = document.createElement('div');
      element.title = outlet.name;
      element.innerHTML = getMapLibreOutletMarkerHtml(
        outlet.name,
        this._markerTheme.getMarkerOptions(isActive, isDisabled),
      );
      element.style.zIndex = isActive ? '1000' : '0';
      element.style.pointerEvents = isDisabled ? 'none' : 'auto';

      if (!isDisabled) {
        element.addEventListener('click', () => this._onMarkerClick(outlet));
      }

      const marker = new this._maplibre.Marker({ element, anchor: 'center' })
        .setLngLat(latLng(outlet as { latitude: number; longitude: number }));

      marker.addTo(this._map);
      this._markers.set(outlet.id, marker);
    });
  }

  private _clearMarkers(): void {
    this._markers.forEach((marker) => marker.remove());
    this._markers.clear();
  }

  private async _tryInitMap(): Promise<void> {
    if (
      this._isDestroyed ||
      !this._isViewInitialized ||
      this._map ||
      this._isMapInitializing ||
      !isPlatformBrowser(this._platformId)
    ) {
      return;
    }

    const initialView = this._getInitialView();

    if (!initialView) {
      return;
    }

    try {
      this._isMapInitializing = true;
      this._maplibre = await import('maplibre-gl');

      if (this._isDestroyed) {
        return;
      }

      configureMapLibreWorker(this._maplibre);
      const { styleUrl, maxZoom } = DEFAULT_MAPLIBRE_CONFIG;
      this._map = new this._maplibre.Map({
        container: this._mapContainer().nativeElement,
        style: styleUrl,
        maxZoom,
        scrollZoom: !this.blockWheelScrolling(),
        // The bottom edge belongs to the list button and the sheet.
        attributionControl: false,
        ...initialView,
      });
      simplifyMapLibreOutletStyle(this._map);
      this._map.addControl(new this._maplibre.AttributionControl({ compact: true }), 'top-left');

      this._renderMarkers();

      if (this.selectedOutlet()) {
        this._centerOn(this.selectedOutlet());
      }
    } finally {
      this._isMapInitializing = false;
    }
  }

  private _getInitialView(): Partial<MapLibre.MapOptions> {
    const positionedOutlets = this.outlets()?.filter(hasCoordinates) || [];
    const selected = this.selectedOutlet();

    if (selected && hasCoordinates(selected)) {
      return {
        center: latLng(selected as { latitude: number; longitude: number }),
        zoom: this._getSelectedOutletZoom(),
      };
    }

    if (positionedOutlets.length === 1) {
      return {
        center: latLng(positionedOutlets[0] as { latitude: number; longitude: number }),
        zoom: DEFAULT_MAPLIBRE_CONFIG.initialZoom,
      };
    }

    if (positionedOutlets.length > 1) {
      return {
        bounds: this._getBoundsLike(positionedOutlets),
        fitBoundsOptions: {
          padding: FIT_BOUNDS_PADDING,
          maxZoom: FIT_BOUNDS_MAX_ZOOM,
          duration: 0,
        },
      };
    }

    return null;
  }

  private _getBoundsLike(outlets: IOutlet[]): [[number, number], [number, number]] {
    let minLat = Number.POSITIVE_INFINITY;
    let minLng = Number.POSITIVE_INFINITY;
    let maxLat = Number.NEGATIVE_INFINITY;
    let maxLng = Number.NEGATIVE_INFINITY;

    outlets.forEach((outlet) => {
      minLat = Math.min(minLat, outlet.latitude);
      minLng = Math.min(minLng, outlet.longitude);
      maxLat = Math.max(maxLat, outlet.latitude);
      maxLng = Math.max(maxLng, outlet.longitude);
    });

    return [[minLng, minLat], [maxLng, maxLat]];
  }

  private _hasSingleOutlet(): boolean {
    return this.outlets()?.length === 1;
  }

  private _getSelectedOutletZoom(): number {
    return this._hasSingleOutlet() ? DEFAULT_MAPLIBRE_CONFIG.initialZoom : SELECTED_OUTLET_ZOOM;
  }

  /**
   * Same as the desktop map: jump straight to the outlet, then only animate the short slide
   * that puts it in the part of the map left visible above the `selected` sheet. Animating the whole trip
   * (easeTo across places and zoom levels) requested tiles for every frame on the way.
   */
  private _centerOn(outlet: IOutlet): void {
    if (!this._map || !hasCoordinates(outlet)) {
      return;
    }

    const sheetHeight = this._host.nativeElement.clientHeight * SELECTED_SHEET_HEIGHT_RATIO;

    this._map.jumpTo({
      center: latLng(outlet as { latitude: number; longitude: number }),
      zoom: this._getSelectedOutletZoom(),
    });
    this._map.panBy([0, Math.round(sheetHeight / 2)], { duration: 800 });
  }

  private _fitBounds(outlets: IOutlet[]): void {
    if (!this._map || !this._maplibre) {
      return;
    }

    const positionedOutlets = outlets.filter(hasCoordinates);

    if (!positionedOutlets.length) {
      return;
    }

    const bounds = positionedOutlets.length > 1 ? createBounds(this._maplibre, positionedOutlets) : null;

    // Deferred on purpose: switching outlets in the accordion collapses the old one first, and the new one
    // is selected right after. By the time this runs it has its own centering, so the reset is dropped.
    setTimeout(() => {
      if (!this._map || this._isDestroyed || this.selectedOutlet()) {
        return;
      }

      if (!bounds) {
        this._map.jumpTo({
          center: latLng(positionedOutlets[0] as { latitude: number; longitude: number }),
          zoom: DEFAULT_MAPLIBRE_CONFIG.initialZoom,
        });
        return;
      }

      this._map.fitBounds(bounds, {
        padding: FIT_BOUNDS_PADDING,
        maxZoom: FIT_BOUNDS_MAX_ZOOM,
        duration: 800,
      });
    }, 50);
  }

  private _syncScrollWheel(blockWheelScrolling: boolean): void {
    if (!this._map) {
      return;
    }

    if (blockWheelScrolling) {
      this._map.scrollZoom.disable();
    } else {
      this._map.scrollZoom.enable();
    }
  }
}
